/** Shared fail-closed website policy. CRM/Lead Ads deliberately does not use this. */
export const WEBSITE_META_DATASET = "930349370697862";
export const WEBSITE_CONSENT_POLICY = "website-marketing-email-ua-v2";
export const META_CANDIDATE_HOSTS = [
  "bodyshapesalon.nl", "www.bodyshapesalon.nl",
  "dieptereiniging.bodyshapesalon.nl", "ems.bodyshapesalon.nl",
  "infrarood.bodyshapesalon.nl", "madero-gezicht.bodyshapesalon.nl",
  "proefles.bodyshapesalon.nl", "neolift.bodyshapesalon.nl",
  "landing.bodyshapesalon.nl", "winteractie.bodyshapesalon.nl",
  "smallgroup.bodyshapesalon.nl", "holidayglow.bodyshapesalon.nl",
] as const;
export type WebsiteMetaEvent = "Lead" | "Schedule";
export type WebsiteConsentSnapshot = {
  marketing: boolean;
  policy: typeof WEBSITE_CONSENT_POLICY;
  decidedAt: number | null;
  source: "cookie_preferences" | "no_choice";
};
export type WebsiteTrackingContext = {
  publicUrl?: string;
  consent: WebsiteConsentSnapshot;
};
export type WebsiteConversion = {
  eligible: boolean;
  eventName?: WebsiteMetaEvent;
  eventId?: string;
  eventTime?: number;
  publicUrl?: string;
};
export const metaFlag = (value: unknown) => value === "true";
const publicAnchors = new Set([
  "slots-h", "booking-section", "form", "signup", "smallgroup-aanvraag",
  "winteractie-aanvraag", "winteractie-prijzen", "landing-social-proof",
  "microneedling-nazorg", "welke-informatie-verzamelen-we",
]);

/** A configured host must ALSO appear in reviewed source inventory. No wildcards. */
export function approvedMetaHosts(value: unknown): string[] {
  if (typeof value !== "string" || value.length > 2000) return [];
  const hosts = value.split(",").map(v => v.trim().toLowerCase()).filter(Boolean);
  if (hosts.some(v => !(META_CANDIDATE_HOSTS as readonly string[]).includes(v))) return [];
  return [...new Set(hosts)];
}

export function publicMetaUrl(value: unknown, hosts: readonly string[]): string | null {
  if (typeof value !== "string" || value.length > 2000 || /[\\\u0000-\u0020]/.test(value)) return null;
  try {
    const url = new URL(value);
    if (url.protocol !== "https:" || url.port || url.username || url.password ||
      !hosts.includes(url.hostname) || !(META_CANDIDATE_HOSTS as readonly string[]).includes(url.hostname)) return null;
    const path = decodeURIComponent(url.pathname).toLowerCase();
    // Normal in-page links (including the booking CTA's #slots-h) are not
    // private routes. Strip them from evidence rather than rejecting the action.
    // Capability/verification fragments and arbitrary encoded payloads stay denied.
    const anchor = decodeURIComponent(url.hash.slice(1));
    if (anchor && !publicAnchors.has(anchor)) return null;
    let operationalQuery = false;
    url.searchParams.forEach((_value, key) => {
      if (/token|key|secret|ticket|code|preview|receipt|verify|^(?:review|copy|voorstel|regional)$/i.test(key)) operationalQuery = true;
    });
    if (path.startsWith('/__') || /^\/(?:api|admin|auth|sign-in|sign-up|login|logout|slideshow|signage|preview|test|testing)(?:[/-]|$)/.test(path) ||
      /^\/(?:blog\/abonnement|evenementen\/verifieer|speciale-(?:avonden|evenementen)\/verifieer|copy-review|tekstvoorstel|beoordelen|review)(?:[/-]|$)/.test(path) ||
      /(?:token|receipt|verifi|unsubscribe|afmeld|preview|feedback|herstel)/i.test(path) ||
      operationalQuery) return null;
    // Attribution remains internal; no query or fragment reaches the transport.
    return `https://${url.hostname === "www.bodyshapesalon.nl" ? "bodyshapesalon.nl" : url.hostname}${url.pathname}`;
  } catch { return null; }
}

export function validWebsiteConsent(value: unknown, now = Date.now()): WebsiteConsentSnapshot {
  const denied: WebsiteConsentSnapshot = { marketing: false, policy: WEBSITE_CONSENT_POLICY, decidedAt: null, source: "no_choice" };
  if (!value || typeof value !== "object") return denied;
  const v = value as Record<string, unknown>;
  if (v.policy !== WEBSITE_CONSENT_POLICY || v.source !== "cookie_preferences" ||
      typeof v.marketing !== "boolean" || typeof v.decidedAt !== "number" ||
      !Number.isSafeInteger(v.decidedAt) || v.decidedAt > now + 60_000 ||
      v.decidedAt < now - 180 * 86400_000) return denied;
  return { marketing: v.marketing, policy: WEBSITE_CONSENT_POLICY, decidedAt: v.decidedAt, source: "cookie_preferences" };
}
